import React from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Package, Clock, Euro, Shield, CheckCircle, ArrowRight, Truck, Users, Zap, ThumbsUp, Building2 } from 'lucide-react';
import { SocialLinks } from '../components/SocialLinks';

function PageHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const links = [
    { to: '/for-customers', label: 'Customers' },
    { to: '/for-drivers', label: 'Drivers' },
    { to: '/for-businesses', label: 'Businesses' },
  ];
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center"><img src="/logo.png" alt="JustDeliver" className="h-9 sm:h-11 w-auto" /></Link>
        <nav className="hidden md:flex items-center gap-1">
          {links.map(l => (
            <Link key={l.to} to={l.to}
              className={`px-3 py-2 text-sm font-semibold rounded-md transition-colors ${location.pathname === l.to ? 'text-[#2E7D32] bg-green-50' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`}
            >{l.label}</Link>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate('/login')} className="px-4 py-2 text-sm font-bold text-gray-700 hover:bg-gray-100 transition-colors rounded-md">Log In</button>
          <button onClick={() => navigate('/register')} className="px-4 py-2 text-sm font-bold bg-[#2E7D32] text-white hover:bg-[#1B5E20] transition-all active:scale-95 rounded-md">Sign Up</button>
        </div>
      </div>
      {/* Mobile nav */}
      <div className="md:hidden flex items-center justify-center gap-1 pb-2 px-4">
        {links.map(l => (
          <Link key={l.to} to={l.to}
            className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${location.pathname === l.to ? 'text-[#2E7D32] bg-green-50' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`}
          >{l.label}</Link>
        ))}
      </div>
    </header>
  );
}

function PageFooter() {
  return (
    <footer className="py-8 border-t border-gray-100 bg-white">
      <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
        <img src="/logo.png" alt="JustDeliver" className="h-7 w-auto" />
        <div className="flex items-center gap-6 text-sm text-gray-500">
          <Link to="/for-customers" className="hover:text-gray-900">Customers</Link>
          <Link to="/for-drivers" className="hover:text-gray-900">Drivers</Link>
          <Link to="/for-businesses" className="hover:text-gray-900">Businesses</Link>
          <Link to="/terms" className="hover:text-gray-900">Terms</Link>
          <Link to="/privacy" className="hover:text-gray-900">Privacy</Link>
        </div>
        <SocialLinks />
      </div>
    </footer>
  );
}

export default function CustomersPage() {
  const navigate = useNavigate();

  const steps = [
    { num: '1', title: 'Post Your Job', desc: 'Tell us what needs moving, where it\'s going, and when. Takes under a minute.', icon: Package },
    { num: '2', title: 'Receive Offers', desc: 'Local drivers see your job and submit their price. Compare and choose.', icon: Users },
    { num: '3', title: 'Get It Delivered', desc: 'Pay securely online. Track your delivery and confirm with a code on arrival.', icon: CheckCircle },
  ];

  const benefits = [
    { icon: Euro, title: 'Save Money', desc: 'Drivers compete for your job — you always get a fair price.' },
    { icon: Zap, title: 'Fast Delivery', desc: 'Get offers within minutes. Most deliveries completed same day.' },
    { icon: ThumbsUp, title: 'Flexible Options', desc: 'Choose your delivery window. Pick the driver that suits you.' },
    { icon: Shield, title: 'Secure & Verified', desc: '6-digit delivery code, proof photos, and verified drivers.' },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="customers-page">
      <PageHeader />

      {/* Hero */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-green-50/50 to-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#2E7D32]">For Customers</span>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900">
            Get Anything Delivered,<br /><span className="text-[#2E7D32]">On Your Terms</span>
          </h1>
          <p className="mt-5 text-lg text-gray-600 max-w-2xl mx-auto">
            Post a delivery job and let drivers compete for it. You compare offers, pick the best price, and track your delivery in real time.
          </p>
          <button onClick={() => navigate('/register')} className="mt-8 inline-flex items-center gap-2 px-8 py-4 bg-[#2E7D32] text-white font-bold text-base hover:bg-[#1B5E20] transition-all active:scale-95 rounded-md" data-testid="customers-cta">
            <Package className="w-5 h-5" /> Post a Delivery Job <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-center text-2xl sm:text-3xl font-bold text-gray-900">How It Works</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-8">
            {steps.map(({ num, title, desc, icon: Icon }) => (
              <div key={num} className="text-center">
                <div className="w-14 h-14 rounded-full bg-[#2E7D32] text-white flex items-center justify-center text-xl font-bold mx-auto">{num}</div>
                <Icon className="w-6 h-6 text-[#2E7D32] mx-auto mt-4" strokeWidth={1.5} />
                <h3 className="mt-3 text-lg font-bold text-gray-900">{title}</h3>
                <p className="mt-2 text-sm text-gray-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-center text-2xl sm:text-3xl font-bold text-gray-900">Why Customers Choose JustDeliver</h2>
          <div className="mt-12 grid sm:grid-cols-2 gap-6">
            {benefits.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white border border-gray-200 rounded-xl p-6 hover:-translate-y-0.5 transition-transform">
                <Icon className="w-6 h-6 text-[#2E7D32]" strokeWidth={1.5} />
                <h3 className="mt-3 text-base font-bold text-gray-900">{title}</h3>
                <p className="mt-1.5 text-sm text-gray-600">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-20 text-center">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Ready to get started?</h2>
          <p className="mt-3 text-gray-600">Free to post. No subscription. Pay only when you accept an offer.</p>
          <button onClick={() => navigate('/register')} className="mt-8 inline-flex items-center gap-2 px-8 py-4 bg-[#2E7D32] text-white font-bold hover:bg-[#1B5E20] transition-all active:scale-95 rounded-md">
            <Package className="w-5 h-5" /> Post a Delivery Job
          </button>
        </div>
      </section>

      <PageFooter />
    </div>
  );
}

export { PageHeader, PageFooter };
